import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { useSessionStorage } from '@/hooks'
import { useCurrentOrganisation } from '../useCurrentOrganisation'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Avatar, AvatarFallback } from '@/components/shadcn/avatar'
import { getInitials } from '@/lib/utils'
import { Separator } from '@/components/shadcn/separator'

// Form validation schema
const formSchema = z.object({
  confirmed: z.boolean().refine((val) => val === true, 'You must confirm that you have reviewed user access'),
})

export type ReviewUsersForm = z.infer<typeof formSchema>

type MessagesWorkspacesNewWorkspaceUserAudit = RestSchemas['Messages.Workspaces.NewWorkspaceUserAudit']

type WorkspaceUser = {
  workspaceId?: number
  userId?: number
  accessGrantedVia?: string | null
  givenName?: string | null
  familyName?: string | null
  email?: string | null
}

type WorkspaceUserGroup = {
  accessType?: string | null
  users?: Array<WorkspaceUser> | null
}

type ReviewUsersFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  workspaceId: number
}

export const ReviewUsersForm = (props: ReviewUsersFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} />
  )
}

const FormComponent = (props: ReviewUsersFormProps) => {
  const queryClient = useQueryClient()
  const { id: orgId } = useCurrentOrganisation()
  const [userId] = useSessionStorage('sub', '')

  const {
    getWorkspacesApiGetWorkspaceUsers,
    postWorkspacesApiAddApprovedWorkspaceUsersAudit,
    getSubscriptionsApiGetOrganisationWorkspacesSummary,
  } = useRestClient()

  const {
    data: workspaceUsers,
    isPending: workspaceUsersLoading,
    isError: workspaceUsersError,
  } = getWorkspacesApiGetWorkspaceUsers.useQuery({
    path: { workspaceId: props.workspaceId },
  })

  const { mutateAsync: submitUserAudit } = postWorkspacesApiAddApprovedWorkspaceUsersAudit.useMutation(undefined, {
    onSuccess: () => {
      // Invalidate multiple queries
      queryClient.invalidateQueries({ queryKey: ['organisationWorkspacesWithAudit', orgId] })
      queryClient.invalidateQueries({ queryKey: ['organisationWorkspaces', orgId] })
      getSubscriptionsApiGetOrganisationWorkspacesSummary.invalidateQueries({ parameters: { query: { orgId } } })
      toast.success('User access review submitted successfully')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error submitting user access review')
    },
  })

  const defaultValues = useMemo(() => {
    return {
      confirmed: false,
    }
  }, [])

  const form = useForm<ReviewUsersForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  // Transform users to ensure uniqueness per group
  const transformedGroups = useMemo(() => {
    if (!workspaceUsers) return []

    return workspaceUsers.map((group: WorkspaceUserGroup) => {
      const uniqueUsers = new Map<number, WorkspaceUser>()

      group.users?.forEach((user) => {
        if (!uniqueUsers.has(user.userId ?? 0)) {
          uniqueUsers.set(user.userId ?? 0, user)
        }
      })

      return {
        accessType: group.accessType || 'Unknown',
        users: [...uniqueUsers.values()],
      }
    })
  }, [workspaceUsers])

  const handleSubmit = async (data: ReviewUsersForm) => {
    if (!data.confirmed) return

    // Transform the data to match API expectations
    const approvedUsers = transformedGroups.flatMap((group) =>
      group.users.map((user) => ({
        user_id: user.userId,
        email: user.email,
      }))
    )

    const requestData: MessagesWorkspacesNewWorkspaceUserAudit = {
      approverId: Number(userId),
      subscriptionId: props.workspaceId,
      approvedUsers,
    }

    await submitUserAudit({ body: requestData })
    form.reset() // Reset form after submission
  }

  if (workspaceUsersLoading) return <FormComponentLoading />
  if (workspaceUsersError) return <ErrorComponent />

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              {/* User Groups Display */}
              {transformedGroups.map((group) => (
                <div key={group.accessType} className="space-y-3">
                  <div className="text-sm font-semibold">{group.accessType}</div>
                  <Separator />
                  <div className="space-y-2">
                    {group.users.map((user) => (
                      <UserCard key={user.userId} user={user} />
                    ))}
                  </div>
                </div>
              ))}

              {/* Confirmation Checkbox */}
              <div className="space-y-4">
                <Separator />
                <FormField
                  control={form.control}
                  name="confirmed"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>I have reviewed user access to this workspace.</FormLabel>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <div className="form-action-container">
              <Button type="submit" disabled={!form.watch('confirmed')}>
                Confirm Review
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  )
}

// User card component (read-only display)
const UserCard = ({ user }: { user: WorkspaceUser }) => {
  const displayName =
    user.givenName || user.familyName
      ? `${user.givenName ?? ''} ${user.familyName ?? ''}`.trim()
      : (user.email ?? 'Unknown User')

  return (
    <div className="flex items-center gap-5 rounded-lg border bg-background p-3">
      <Avatar className="ring-2 ring-background">
        <AvatarFallback className="bg-accent text-sm text-accent-foreground">{getInitials(displayName)}</AvatarFallback>
      </Avatar>
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium">{displayName}</span>
        {user.email && <span className="text-xs text-muted-foreground">{user.email}</span>}
      </div>
    </div>
  )
}
